import { forwardRef } from "react";
import type { ComponentProps, ElementRef, PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

type TitleProps = {
  value: string;
} & ComponentProps<"div">;

export const Title = forwardRef<ElementRef<"div">, TitleProps>(
  ({ className, value, ...rest }, ref) => (
    <h3
      className={twMerge(
        "pt-10 text-2xl text-neutral-900 dark:text-white",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {value}
    </h3>
  ),
);
Title.displayName = "PerformanceDetails.Title";

export const Section = forwardRef<
  ElementRef<"div">,
  ComponentProps<"div"> & PropsWithChildren
>(({ className, children, ...rest }, ref) => (
  <div className={twMerge("pt-6", className)} ref={ref} {...rest}>
    {children}
  </div>
));
Section.displayName = "PerformanceDetails.Section";

type SubtitleProps = ComponentProps<"div"> & PropsWithChildren;

export const Subtitle = forwardRef<ElementRef<"div">, SubtitleProps>(
  ({ className, children, ...rest }, ref) => (
    <p
      className={twMerge(
        "text-neutral-700 dark:text-neutral-variant-70",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </p>
  ),
);
Subtitle.displayName = "PerformanceDetails.Subtitle";

type CardProps = ComponentProps<"div"> & PropsWithChildren;

export const Card = forwardRef<ElementRef<"div">, CardProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      className={twMerge(
        "w-full rounded-xl bg-surface-container-highest p-5 text-neutral-900 dark:bg-surface-container-default-dark dark:text-primary-80",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  ),
);
Card.displayName = "PerformanceDetails.Card";

export const CardHeader = forwardRef<
  ElementRef<"div">,
  ComponentProps<"div"> & PropsWithChildren
>(({ className, children, ...rest }, ref) => (
  <div
    className={twMerge("flex justify-between pb-4", className)}
    ref={ref}
    {...rest}
  >
    {children}
  </div>
));
CardHeader.displayName = "PerformanceDetails.CardHeader";

export const CardTitle = forwardRef<
  ElementRef<"div">,
  ComponentProps<"div"> & PropsWithChildren
>(({ className, children, ...rest }, ref) => (
  <div className={twMerge("flex gap-2", className)} ref={ref} {...rest}>
    {children}
  </div>
));
CardTitle.displayName = "PerformanceDetails.CardTitle";

type QualifiedTagProps = ComponentProps<"span"> & PropsWithChildren;

export const QualifiedTag = forwardRef<ElementRef<"span">, QualifiedTagProps>(
  ({ className, children, ...rest }, ref) => (
    <span
      className={twMerge(
        "w-fit rounded-full p-2 text-xs text-white",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </span>
  ),
);
QualifiedTag.displayName = "PerformanceDetails.QualifiedTag";

export const CardBody = forwardRef<
  ElementRef<"div">,
  ComponentProps<"div"> & PropsWithChildren
>(({ className, children, ...rest }, ref) => (
  <div
    className={twMerge("grid grid-cols-3 gap-3", className)}
    ref={ref}
    {...rest}
  >
    {children}
  </div>
));
CardBody.displayName = "PerformanceDetails.CardBody";

type CardResultProps = ComponentProps<"div"> & PropsWithChildren;

export const CardResult = forwardRef<ElementRef<"div">, CardResultProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      className={twMerge(
        "rounded-lg bg-surface-container-high p-4 pr-12 text-secondary-20 dark:bg-surface-container-high-dark dark:text-primary",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  ),
);
CardResult.displayName = "PerformanceDetails.CardResult";

type CardResultValueProps = ComponentProps<"p"> & PropsWithChildren;

export const CardResultValue = forwardRef<
  ElementRef<"p">,
  CardResultValueProps
>(({ className, children, ...rest }, ref) => (
  <p className={className} ref={ref} {...rest}>
    {children}
  </p>
));
CardResultValue.displayName = "PerformanceDetails.CardResultValue";
